import { useTicketsFilterOptions } from "@jugl-web/domain-resources/tickets/hooks/useTicketsFilterOptions";
import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import {
  CX_INBOX_SIDEBAR_PINNED_KEY,
  CX_TICKETS_SIDEBAR_EXPANDED_KEY,
  CX_TICKETS_SIDEBAR_MANUAL_WIDTH_KEY,
} from "@jugl-web/utils/storage";
import {
  ResizableSidebarWrapper,
  useResizableSidebar,
} from "@web-src/modules/common/components/ResizableSidebar";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useClientSupportPageContext } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { useInstagramAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useCallback, useMemo, useState } from "react";
import { collapsedIconButtonClassName } from "../../consts";
import { useIsInboxSidebarCompact } from "../../hooks/useIsInboxSidebarCompact";
import { ReactComponent as ClosePanelIcon } from "./assets/close-panel.svg";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as OpenPanelIcon } from "./assets/open-panel.svg";
import { ReactComponent as PhoneCallsIcon } from "./assets/voice.svg";
import { ReactComponent as PinIcon } from "./assets/pin.svg";
import { ReactComponent as UnpinIcon } from "./assets/unpin.svg";
import { ReactComponent as SmsIcon } from "./assets/sms.svg";
import { ReactComponent as WebChatIcon } from "./assets/web-chat.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { NavItem } from "./components/NavItem";
import { SidebarSectionHeader } from "./components/SidebarSectionHeader";

const INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH = 240;
const INBOX_SIDEBAR_MAX_WIDTH = 240;
const INBOX_SIDEBAR_MIN_WIDTH = 80;
const INBOX_SIDEBAR_COLLAPSE_THRESHOLD = 165;

export const InboxSidebar: FC = () => {
  const { entityId } = useEntitySelectedProvider();

  const {
    availableChannels,
    selectedView,
    selectView,
    openCreateTicketSidebar,
  } = useClientSupportPageContext();

  const { openManageAccountDrawer, crmConfig } = useManageAccountsDrawer();

  const { isConnected: isWhatsappConnected } = useWhatsappAccount();
  const { isConnected: isInstagramConnected } = useInstagramAccount();
  const { isConnected: isWebChatConnected } = useWebChatBotAccount();
  const { isConnected: isEmailConnected } = useEmailAccount();
  const { unreadCounts } = useCRMUnreadIndicators();
  const { isCx } = useAppVariant();
  const { filterOptions: ticketFilterOptions } = useTicketsFilterOptions();
  const { isTicketCreationBlocked } = useTicketsLimit();

  const [isInboxSidebarPinned, setIsInboxSidebarPinned] = useLocalStorage(
    CX_INBOX_SIDEBAR_PINNED_KEY,
    true
  );
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);

  const isSidebarCompact = useIsInboxSidebarCompact();

  const { isSidebarExpanded, sidebarWidth, minWidth, updateSidebarWidth } =
    useResizableSidebar({
      expandedStorageKey: CX_TICKETS_SIDEBAR_EXPANDED_KEY,
      widthStorageKey: CX_TICKETS_SIDEBAR_MANUAL_WIDTH_KEY,
      defaultExpandedWidth: INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH,
      maxWidth: INBOX_SIDEBAR_MAX_WIDTH,
      minWidth: INBOX_SIDEBAR_MIN_WIDTH,
      collapseThreshold: INBOX_SIDEBAR_COLLAPSE_THRESHOLD,
    });

  const { ticketsApi } = useRestApiProvider();

  const { data: ticketsStats } = ticketsApi.useGetTicketsStatsQuery(
    {
      entityId,
    },
    { refetchOnMountOrArgChange: true }
  );

  const { t } = useTranslations();

  const getChannelIcon = useCallback((channelId: CRMChannel) => {
    switch (channelId) {
      case CRMChannel.whatsapp:
        return <WhatsappIcon className="shrink-0" />;
      case CRMChannel.instagram:
        return <InstagramIcon className="shrink-0" />;
      case CRMChannel.chatbot:
        return <WebChatIcon className="shrink-0" />;
      case CRMChannel.twilio:
        return <SmsIcon className="shrink-0" />;
      case CRMChannel.voice:
        return <PhoneCallsIcon className="shrink-0" />;
      case CRMChannel.email:
        return <EmailIcon className="shrink-0" />;
      default:
        return <WebChatIcon className="shrink-0" />;
    }
  }, []);

  const getChannelLabel = useCallback(
    (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return t({
            id: "client-support-page.whatsapp",
            defaultMessage: "WhatsApp",
          });
        case CRMChannel.instagram:
          return t({
            id: "client-support-page.instagram",
            defaultMessage: "Instagram",
          });
        case CRMChannel.chatbot:
          return t({
            id: "client-support-page.web-chat",
            defaultMessage: "Web Chat",
          });
        case CRMChannel.twilio:
          return t({
            id: "client-support-page.twilio",
            defaultMessage: "Text (SMS)",
          });
        case CRMChannel.voice:
          return t({
            id: "client-support-page.voice",
            defaultMessage: "Phone calls",
          });
        case CRMChannel.email:
          return t({
            id: "client-support-page.email",
            defaultMessage: "Email",
          });
        default:
          return t({
            id: "client-support-page.unknown-channel",
            defaultMessage: "Unknown Channel",
          });
      }
    },
    [t]
  );

  const isChannelConnected = useCallback(
    (channelId: CRMChannel) => {
      switch (channelId) {
        case CRMChannel.whatsapp:
          return isWhatsappConnected;
        case CRMChannel.instagram:
          return isInstagramConnected;
        case CRMChannel.chatbot:
          return isWebChatConnected;
        case CRMChannel.twilio:
          return !!crmConfig?.twilio?.id;
        case CRMChannel.voice:
          return !!crmConfig?.voice?.id;
        case CRMChannel.email:
          return isEmailConnected;
        default:
          return true;
      }
    },
    [
      isWhatsappConnected,
      isInstagramConnected,
      isWebChatConnected,
      isEmailConnected,
      crmConfig?.twilio?.id,
      crmConfig?.voice?.id,
    ]
  );

  const getUnreadCountForChannel = useCallback(
    (channel: CRMChannel): number => {
      if (!unreadCounts) {
        return 0;
      }

      switch (channel) {
        case CRMChannel.whatsapp:
          return unreadCounts.wa_unread ?? 0;
        case CRMChannel.instagram:
          return unreadCounts.ig_unread ?? 0;
        case CRMChannel.chatbot:
          return unreadCounts.chatbot_unread ?? 0;
        case CRMChannel.twilio:
          return unreadCounts.twilio_unread ?? 0;
        case CRMChannel.voice:
          return unreadCounts.voice_unread ?? 0;
        case CRMChannel.email:
          return unreadCounts.email_unread ?? 0;
        default:
          return 0;
      }
    },
    [unreadCounts]
  );

  const channelItems = useMemo(
    () =>
      availableChannels.map((channel) => ({
        id: channel.id,
        label: channel.name || getChannelLabel(channel.id),
        icon: getChannelIcon(channel.id),
        count: getUnreadCountForChannel(channel.id),
        isDisconnected: !isChannelConnected(channel.id),
      })),
    [
      availableChannels,
      getChannelIcon,
      getChannelLabel,
      getUnreadCountForChannel,
      isChannelConnected,
    ]
  );

  const handleToggleSidebar = () => {
    updateSidebarWidth(
      isSidebarExpanded ? minWidth : INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH
    );
  };

  const handleTogglePinned = () => {
    setIsHoverExpanded(false);
    setIsInboxSidebarPinned((previous) => !previous);
  };

  const visibleTicketFilterOptions = useMemo(
    () =>
      ticketFilterOptions.filter(
        (option) => option.id !== "todo-today" && option.id !== "recents"
      ),
    [ticketFilterOptions]
  );

  const hasAllChannelsConnected =
    availableChannels.length >= Object.keys(CRMChannel).length;

  const renderSidebar = (isExpanded: boolean) => (
    <>
      <div
        className={cx(
          "flex h-[60px] shrink-0 items-center gap-3 border-0 border-b border-solid border-dark-100 px-6",
          !isExpanded && "justify-center px-0"
        )}
      >
        {isExpanded && (
          <span className="truncate font-secondary text-lg font-medium text-dark">
            {t({
              id: "client-support-page.inbox-sidebar.inbox",
              defaultMessage: "Inbox",
            })}
          </span>
        )}
        <div className={cx("flex items-center gap-2", isExpanded && "ml-auto")}>
          {!isSidebarCompact && (!isInboxSidebarPinned || isExpanded) && (
            <Tooltip
              placement="right"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  onClick={handleTogglePinned}
                  className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                >
                  {isInboxSidebarPinned ? <UnpinIcon /> : <PinIcon />}
                </PlainButton>
              )}
            >
              {isInboxSidebarPinned
                ? t({
                    id: "client-support-page.inbox-sidebar.unpin-panel",
                    defaultMessage: "Unpin Panel",
                  })
                : t({
                    id: "client-support-page.inbox-sidebar.pin-panel",
                    defaultMessage: "Pin Panel",
                  })}
            </Tooltip>
          )}
          {!isSidebarCompact && isInboxSidebarPinned && (
            <Tooltip
              placement="right"
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  ref={ref}
                  {...props}
                  onClick={handleToggleSidebar}
                  className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                >
                  {isSidebarExpanded ? <ClosePanelIcon /> : <OpenPanelIcon />}
                </PlainButton>
              )}
            >
              {isSidebarExpanded
                ? t({
                    id: "client-support-page.inbox-sidebar.collapse-menu",
                    defaultMessage: "Collapse Menu",
                  })
                : t({
                    id: "client-support-page.inbox-sidebar.expand-menu",
                    defaultMessage: "Expand Menu",
                  })}
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={cx(
          "jugl__custom-scrollbar flex flex-col overflow-x-hidden overflow-y-auto py-6",
          isSidebarCompact && "max-sm:py-4"
        )}
      >
        <div
          className={cx(
            "flex flex-col gap-3 px-4",
            isSidebarCompact && "max-sm:gap-2 max-sm:px-2"
          )}
        >
          <SidebarSectionHeader
            title={t({
              id: "client-support-page.inbox-sidebar.channels",
              defaultMessage: "Channels",
            })}
            isExpanded={isExpanded}
            showAddButton={!hasAllChannelsConnected}
            addButtonTooltip={t({
              id: "client-support-page.inbox-sidebar.connect-new-channel",
              defaultMessage: "Connect new Channel",
            })}
            onAdd={() =>
              openManageAccountDrawer({ name: "connectedAccountList" })
            }
          />
          {channelItems.map((item) => (
            <NavItem
              key={item.id}
              label={item.label}
              icon={item.icon}
              isActive={
                selectedView.kind === "channel" &&
                selectedView.channel === item.id
              }
              isExpanded={isExpanded}
              isDisconnected={item.isDisconnected}
              count={item.count}
              onClick={() => selectView({ kind: "channel", channel: item.id })}
            />
          ))}
        </div>

        {isCx && (
          <>
            <div
              className={cx(
                "my-5 h-px w-full bg-dark-100",
                isSidebarCompact && "max-sm:my-3"
              )}
            />

            <div
              className={cx(
                "flex flex-col gap-3 px-4",
                isSidebarCompact && "max-sm:gap-2 max-sm:px-2"
              )}
            >
              <SidebarSectionHeader
                title={t({
                  id: "client-support-page.inbox-sidebar.tickets",
                  defaultMessage: "Tickets",
                })}
                isExpanded={isExpanded}
                addButtonTooltip={t({
                  id: "client-support-page.inbox-sidebar.create-ticket",
                  defaultMessage: "Create Ticket",
                })}
                isAddDisabled={isTicketCreationBlocked}
                renderDisabledTooltip={(renderTrigger) => (
                  <TicketCreationBlockedTooltip renderTrigger={renderTrigger} />
                )}
                onAdd={() => openCreateTicketSidebar()}
              />
              {visibleTicketFilterOptions.map((item) => (
                <NavItem
                  key={item.id}
                  label={item.label}
                  icon={item.icon}
                  isActive={
                    selectedView.kind === "tickets" &&
                    selectedView.filter === item.id
                  }
                  isExpanded={isExpanded}
                  count={ticketsStats?.[item.id] ?? 0}
                  onClick={() =>
                    selectView({ kind: "tickets", filter: item.id })
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );

  // On narrow viewports the rail is locked to its compact form
  if (isSidebarCompact) {
    return (
      <div className="flex h-full w-20 shrink-0 flex-col border-0 border-r border-solid border-dark-100 bg-white max-sm:w-14">
        {renderSidebar(false)}
      </div>
    );
  }

  if (!isInboxSidebarPinned) {
    const overlaySidebarWidth = isSidebarExpanded
      ? sidebarWidth
      : INBOX_SIDEBAR_DEFAULT_EXPANDED_WIDTH;
    const visibleSidebarWidth = isHoverExpanded
      ? overlaySidebarWidth
      : INBOX_SIDEBAR_MIN_WIDTH;

    return (
      <div
        className={cx(
          "relative z-50 flex h-full shrink-0 flex-col overflow-hidden border-0 border-r border-solid border-dark-100 bg-white transition-[width,margin-right] duration-150 ease-linear",
          isHoverExpanded && "shadow-[4px_0_24px_rgba(0,0,0,0.12)]"
        )}
        style={{
          width: `${visibleSidebarWidth}px`,
          marginRight: isHoverExpanded
            ? `${INBOX_SIDEBAR_MIN_WIDTH - visibleSidebarWidth}px`
            : 0,
        }}
        onMouseEnter={() => setIsHoverExpanded(true)}
        onMouseLeave={() => setIsHoverExpanded(false)}
        onFocus={() => setIsHoverExpanded(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setIsHoverExpanded(false);
          }
        }}
      >
        {renderSidebar(isHoverExpanded)}
      </div>
    );
  }

  return (
    <ResizableSidebarWrapper
      sidebarWidth={sidebarWidth}
      onWidthChange={updateSidebarWidth}
    >
      {(isResizing) => (
        <div
          className={cx(
            "flex h-full shrink-0 flex-col border-0 border-r border-solid border-dark-100 bg-white transition-[width] duration-300",
            isResizing && "transition-none"
          )}
          style={{ width: `${sidebarWidth}px` }}
        >
          {renderSidebar(isSidebarExpanded)}
        </div>
      )}
    </ResizableSidebarWrapper>
  );
};
